import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * GoldsmithFormDialog — Phase 11
 *
 * Inline full-form Goldsmith creation. Used in the New Order Form dialog
 * (and reusable elsewhere) so users don't have to context-switch to the
 * Goldsmiths page just to add a new one mid-order.
 */
export function GoldsmithFormDialog({ open, onOpenChange, companyId, onCreated }) {
  const empty = { name: '', phone: '', email: '', address: '', specialization: '', notes: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Goldsmith name is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/masters/goldsmiths', { ...form, company_id: companyId });
      toast.success(`Goldsmith "${data.name}" created`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(extractError(e, 'Goldsmith create failed')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inline-goldsmith-dialog">
        <DialogHeader>
          <DialogTitle>New Goldsmith</DialogTitle>
          <DialogDescription>Capture goldsmith details. You can edit further from the Goldsmiths page.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name <span className="text-rose-600">*</span></Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inline-goldsmith-name-input" />
          </div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inline-goldsmith-phone-input" /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="col-span-2"><Label>Specialization</Label><Input value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} placeholder="e.g. Bridal jewellery, repair, etc." /></div>
          <div className="col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" disabled={saving || !form.name.trim()} data-testid="inline-goldsmith-submit-button">{saving ? 'Saving…' : 'Create Goldsmith'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GoldsmithFormDialog;
